// Google Analytics 4 — client-side helpers.
// Respects cookie consent: writes to `window.dataLayer` only after the user
// has accepted the cookie banner ("ns_cookie_consent_v1" in localStorage).
//
// Usage:
//   import { gaEvent, gaPageview } from "@/lib/gtag";
//   gaEvent("quiz_started", { quiz_id: "abc", quiz_title: "Geometria" });

export const GA_MEASUREMENT_ID =
  process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || "";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

const CONSENT_KEY = "ns_cookie_consent_v1";

export function hasAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { decision?: string } | null;
    return parsed?.decision === "accept";
  } catch {
    return false;
  }
}

function canTrack(): boolean {
  if (typeof window === "undefined") return false;
  if (!GA_MEASUREMENT_ID) return false;
  if (typeof window.gtag !== "function") return false;
  if (!hasAnalyticsConsent()) return false;
  return true;
}

export function gaPageview(url: string, title?: string): void {
  if (!canTrack()) return;
  window.gtag!("config", GA_MEASUREMENT_ID, {
    page_path: url,
    page_title: title,
    anonymize_ip: true,
  });
}

// Generic GA4 event helper.
export function gaEvent(
  action: string,
  params: Record<string, unknown> = {}
): void {
  if (!canTrack()) return;
  window.gtag!("event", action, params);
}

export function metaPixelEvent(
  event: string,
  params: Record<string, unknown> = {}
): void {
  if (typeof window === "undefined" || !hasAnalyticsConsent()) return;
  if (typeof window.fbq !== "function") return;
  window.fbq("track", event, params);
}

// ─── Domain helpers ────────────────────────────────────────────────
type QuizStartedParams = { quiz_id: string; quiz_title?: string };
type QuizCompletedParams = {
  quiz_id: string;
  score: number;
  duration_seconds: number;
};
type QuizAbandonedParams = { quiz_id: string; question_reached: number };
type ArticleReadParams = {
  article_id: string;
  title: string;
  category?: string;
  read_percent: number;
};
type NewsletterSource = "blog" | "popup" | "homepage" | string;
type AuthMethod = "email" | "google" | "facebook" | string;

export const trackQuizStarted = (p: QuizStartedParams) =>
  gaEvent("quiz_started", p);
export const trackQuizCompleted = (p: QuizCompletedParams) =>
  gaEvent("quiz_completed", p);
export const trackQuizAbandoned = (p: QuizAbandonedParams) =>
  gaEvent("quiz_abandoned", p);
export const trackArticleRead = (p: ArticleReadParams) =>
  gaEvent("article_read", p);
export const trackNewsletterSignup = (source: NewsletterSource) =>
  gaEvent("newsletter_signup", { source });
export const trackUserRegistered = (method: AuthMethod) =>
  gaEvent("user_registered", { method });
export const trackUserLogin = (method: AuthMethod) =>
  gaEvent("user_login", { method });
export const trackSearchPerformed = (query: string, resultsCount: number) =>
  gaEvent("search_performed", { query, results_count: resultsCount });
export const trackButtonClick = (buttonName: string, page?: string) =>
  gaEvent("button_click", {
    button_name: buttonName,
    page: page ?? (typeof window !== "undefined" ? window.location.pathname : ""),
  });
